import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { ArrowRight, UserMinus } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { SpecialistAssignmentService } from '@/lib/services/specialist-assignment-service'
import type { AuditLogEntry } from '@/types/specialist-assignment'
import { format } from 'date-fns'

interface AssignmentHistoryModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  caregiverId: string
  caregiverName: string
}

export function AssignmentHistoryModal({
  open,
  onOpenChange,
  caregiverId,
  caregiverName
}: AssignmentHistoryModalProps) {
  const [history, setHistory] = useState<AuditLogEntry[]>([])
  const [loading, setLoading] = useState(false)
  const { toast } = useToast()

  useEffect(() => {
    if (open) {
      loadHistory()
    }
  }, [open, caregiverId])

  const loadHistory = async () => {
    setLoading(true)
    try {
      const data = await SpecialistAssignmentService.getCaregiverHistory(caregiverId)
      setHistory(data)
    } catch (error) {
      console.error('Error loading history:', error)
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load assignment history'
      })
    } finally {
      setLoading(false)
    }
  }

  const getActionDisplay = (entry: AuditLogEntry) => {
    switch (entry.action) {
      case 'transferred':
        return (
          <div className="flex items-center gap-2">
            <span className="font-medium">{entry.old_specialist_name}</span>
            <ArrowRight className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">{entry.new_specialist_name}</span>
            <Badge variant="outline">Transferred</Badge>
          </div>
        )
      case 'assigned':
        return (
          <div className="flex items-center gap-2">
            <span className="font-medium">{entry.new_specialist_name}</span>
            <Badge variant="outline">Assigned</Badge>
          </div>
        )
      case 'unassigned':
        return (
          <div className="flex items-center gap-2">
            <UserMinus className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">{entry.old_specialist_name}</span>
            <Badge variant="destructive">Unassigned</Badge>
          </div>
        )
      case 'deactivated':
        return (
          <div className="flex items-center gap-2">
            <span className="font-medium">{entry.old_specialist_name}</span>
            <Badge variant="secondary">Deactivated</Badge>
          </div>
        )
      default:
        return <Badge>{entry.action}</Badge>
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Assignment History</DialogTitle>
          <DialogDescription>
            Specialist assignment history for {caregiverName}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <Skeleton key={i} className="h-20 w-full" />
              ))}
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No assignment history found
            </div>
          ) : (
            <div className="space-y-3">
              {history.map((entry) => (
                <div
                  key={entry.id}
                  className="border rounded-lg p-4 space-y-2"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {getActionDisplay(entry)}
                    </div>
                    <time className="text-sm text-muted-foreground whitespace-nowrap ml-4">
                      {format(new Date(entry.created_at), 'MMM d, yyyy h:mm a')}
                    </time>
                  </div>
                  
                  {entry.reason && (
                    <div className="text-sm">
                      <span className="font-medium">Reason:</span>{' '}
                      <span className="text-muted-foreground">{entry.reason}</span>
                    </div>
                  )}
                  
                  <div className="text-xs text-muted-foreground">
                    By {entry.performed_by_email}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
